<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { drawRoadWatermark, loadRoadWatermark } from '../domain/roadWatermark.js';
import { runtimeLogger } from '../services/runtimeLogger.js';
import { drawCenteredRoadText, drawGlassDot, drawGlassRing, drawGlassSlash, drawRoadBoard, drawRoadGridLine, drawTieArc, ROAD_THEMES } from '../domain/roadVisual.js';
import { latestChangedVisibleRoadIndex, resolveRoadModel, shouldFlashRoadResult } from '../domain/roadFlash.js';

const props = defineProps({
  analysis: { type: Object, default: () => ({}) },
  mode: { type: String, default: 'main' },
  seed: { type: Number, default: 1 },
  tableKey: { type: [Number, String], default: null },
  flashSeq: { type: Number, default: 0 }
});
const canvas = ref(null);
let newestBlinkHidden = false;
let newestBlinkInterval = 0;
let blinkRoadIndexes = [-1, -1, -1, -1];

function roadSources() {
  const model = resolveRoadModel(props.analysis, props.mode);
  return [model.DaLu || '', ...(Array.isArray(model.XiaoLu) ? model.XiaoLu : [])];
}

function resultRoadSources() {
  const model = resolveRoadModel(props.analysis, 'main');
  return [model.DaLu || '', ...(Array.isArray(model.XiaoLu) ? model.XiaoLu : [])];
}

function latestChangedVisibleIndex(previousValue, nextValue) {
  const previous = String(previousValue || '').split('@');
  const next = String(nextValue || '').split('@');
  for (let index = Math.min(next.length - 1, 240); index >= 1; index -= 1) {
    const value = String(next[index] || '');
    if (value && value !== '0' && value !== String(previous[index] || '')) return index;
  }
  return -1;
}

function flashNewestRoadMarks() {
  window.clearInterval(newestBlinkInterval);
  let toggles = 0;
  newestBlinkHidden = false;
  newestBlinkInterval = window.setInterval(() => {
    toggles += 1;
    newestBlinkHidden = !newestBlinkHidden;
    draw();
    if (toggles >= 6) {
      window.clearInterval(newestBlinkInterval);
      newestBlinkInterval = 0;
      newestBlinkHidden = false;
      draw();
    }
  }, 240);
}

function draw() {
  const node = canvas.value;
  if (!node) return;
  const ctx = node.getContext('2d');
  const width = 1082;
  const height = 328;
  node.width = width;
  node.height = height;
  const ink = '#1f2121';
  drawRoadBoard(ctx, width, height);
  for (let y = 0; y < 6; y += 1) {
    drawRoadGridLine(ctx, 0, y * 54, 1080, y * 54, true);
    drawRoadGridLine(ctx, 0, y * 54 + 27, 1080, y * 54 + 27);
  }
  for (let x = 0; x < 21; x += 1) drawRoadGridLine(ctx, x * 54, 0, x * 54, height, true);
  for (let x = 0; x < 40; x += 1) drawRoadGridLine(ctx, x * 27, 0, x * 27, height);
  drawRoadWatermark(ctx, width, height, { widthRatio: .43, opacity: .105 });

  const parseRoad = (value) => String(value || '').split('@');
  const model = resolveRoadModel(props.analysis, 'main');
  const mainRoad = parseRoad(model.DaLu);
  const derived = Array.isArray(model.XiaoLu) ? model.XiaoLu.map(parseRoad) : [];
  const latestMainRoadIndex = blinkRoadIndexes[0];
  const latestDerivedIndexes = blinkRoadIndexes.slice(1);
  const roadValue = (road, col, row) => String(road?.[col + row * 40 + 1] || '');
  const fallbackVisible = (col, row, salt, limit) => col < limit && ((col * 7 + row * 11 + props.seed * 13 + salt) % 23) < 10;
  const fallbackColor = (col, row, salt) => ((col * 5 + row * 3 + props.seed * 7 + salt) % 13) < 7 ? 'red' : 'blue';

  for (let row = 0; row < 6; row += 1) {
    for (let col = 0; col < 40; col += 1) {
      const mainValue = roadValue(mainRoad, col, row);
      if ((mainValue && mainValue !== '0' || (!mainRoad.length && fallbackVisible(col, row, 1, 27 + props.seed * 2))) && !(newestBlinkHidden && (col + row * 40 + 1) === latestMainRoadIndex)) {
        const sourceIndex = col + row * 40 + 1;
        const code = mainValue && mainValue !== '0' ? mainValue.charAt(0) : (fallbackColor(col, row, 1) === 'red' ? 'A' : 'B');
        const color = ['A', 'C'].includes(code) ? 'red' : 'blue';
        const cx = col * 27 + 13;
        const cy = row * 27 + 13;
        drawGlassRing(ctx, cx, cy, 11, ROAD_THEMES[color], 2.2);
        const tieCount = Number.parseInt(mainValue.slice(1), 10) || 0;
        if (code === 'C' || code === 'D' || tieCount > 0 || (!mainRoad.length && (col * 3 + row + props.seed) % 17 === 0)) {
          drawTieArc(ctx, cx, cy, 11, 4);
          if (tieCount > 0) {
            ctx.fillStyle = ink;
            ctx.font = '14px Noto Sans SC';
            drawCenteredRoadText(ctx, String(tieCount), cx, cy);
          }
        }
      }

      const bigEye = roadValue(derived[0], col, row);
      if ((bigEye && bigEye !== '0' || (!derived[0]?.length && fallbackVisible(col, row, 5, 31))) && !(newestBlinkHidden && (col + row * 40 + 1) === latestDerivedIndexes[0])) {
        const sourceIndex = col + row * 40 + 1;
        const color = bigEye && bigEye !== '0' ? (bigEye.charAt(0) === 'A' ? 'red' : 'blue') : fallbackColor(col, row, 5);
        const cx = col * 13.5 + 8;
        const cy = row * 13.5 + 169;
        drawGlassRing(ctx, cx, cy, 5, ROAD_THEMES[color], 1.8);
      }

      const smallRoad = roadValue(derived[1], col, row);
      if ((smallRoad && smallRoad !== '0' || (!derived[1]?.length && fallbackVisible(col, row, 9, 29))) && !(newestBlinkHidden && (col + row * 40 + 1) === latestDerivedIndexes[1])) {
        const sourceIndex = col + row * 40 + 1;
        const color = smallRoad && smallRoad !== '0' ? (smallRoad.charAt(0) === 'C' ? 'red' : 'blue') : fallbackColor(col, row, 9);
        const cx = col * 13.5 + 8;
        const cy = row * 13.5 + 250;
        drawGlassDot(ctx, cx, cy, 5, ROAD_THEMES[color]);
      }

      const cockroach = roadValue(derived[2], col, row);
      if ((cockroach && cockroach !== '0' || (!derived[2]?.length && fallbackVisible(col, row, 13, 37))) && !(newestBlinkHidden && (col + row * 40 + 1) === latestDerivedIndexes[2])) {
        const sourceIndex = col + row * 40 + 1;
        const color = cockroach && cockroach !== '0' ? (cockroach.charAt(0) === 'E' ? 'red' : 'blue') : fallbackColor(col, row, 13);
        const x = col * 13.5 + 544;
        const y = row * 13 + 255;
        drawGlassSlash(ctx, x, y + 8, x + 7, y, ROAD_THEMES[color], 3);
      }
    }
  }

  if (props.mode !== 'main') {
    const previewModel = resolveRoadModel(props.analysis, props.mode);
    const mainSources = [model.DaLu || '', ...(Array.isArray(model.XiaoLu) ? model.XiaoLu : [])];
    const previewSources = [previewModel.DaLu || '', ...(Array.isArray(previewModel.XiaoLu) ? previewModel.XiaoLu : [])];
    const previewIndexes = previewSources.map((value, index) => latestChangedVisibleRoadIndex(mainSources[index], value, { sourceColumns: 40, visibleColumns: 40, visibleRows: 6 }));
    const previewRoads = previewSources.map(parseRoad);

    previewIndexes.forEach((index, roadIndex) => {
      if (index < 1) return;
      const zeroBased = index - 1;
      const col = zeroBased % 40;
      const row = Math.floor(zeroBased / 40);
      const value = String(previewRoads[roadIndex]?.[index] || '');
      if (!value || value === '0') return;
      if (roadIndex === 0) {
        const code = value.charAt(0);
        const color = ['A', 'C'].includes(code) ? 'red' : 'blue';
        const cx = col * 27 + 13;
        const cy = row * 27 + 13;
        drawGlassRing(ctx, cx, cy, 11, ROAD_THEMES[color], 2.2);
        const tieCount = Number.parseInt(value.slice(1), 10) || 0;
        if (code === 'C' || code === 'D' || tieCount > 0) {
          drawTieArc(ctx, cx, cy, 11, 4);
          if (tieCount > 0) {
            ctx.fillStyle = ink;
            ctx.font = '14px Noto Sans SC';
            drawCenteredRoadText(ctx, String(tieCount), cx, cy);
          }
        }
      } else if (roadIndex === 1) {
        const color = value.charAt(0) === 'A' ? 'red' : 'blue';
        drawGlassRing(ctx, col * 13.5 + 8, row * 13.5 + 169, 5, ROAD_THEMES[color], 1.8);
      } else if (roadIndex === 2) {
        const color = value.charAt(0) === 'C' ? 'red' : 'blue';
        drawGlassDot(ctx, col * 13.5 + 8, row * 13.5 + 250, 5, ROAD_THEMES[color]);
      } else if (roadIndex === 3) {
        const color = value.charAt(0) === 'E' ? 'red' : 'blue';
        const x = col * 13.5 + 544;
        const y = row * 13 + 255;
        drawGlassSlash(ctx, x, y + 8, x + 7, y, ROAD_THEMES[color], 3);
      }
    });
  }
}

onMounted(() => {
  draw();
  loadRoadWatermark().then(draw).catch((error) => runtimeLogger.error('[wanli-road] hall watermark failed', error));
});
onBeforeUnmount(() => window.clearInterval(newestBlinkInterval));
watch(roadSources, (next, previous = []) => {
  if (props.tableKey !== null) return;
  if (!previous.length) return;
  blinkRoadIndexes = next.map((value, index) => latestChangedVisibleIndex(previous[index], value));
  if (blinkRoadIndexes.some((index) => index >= 1)) flashNewestRoadMarks();
});
watch(() => [props.tableKey, props.flashSeq, ...resultRoadSources()], (next, previous = []) => {
  if (props.tableKey === null) return;
  const [nextTableKey, nextSeq, ...nextSources] = next;
  const [previousTableKey, previousSeq, ...previousSources] = previous;
  if (!shouldFlashRoadResult({ previousTableKey, nextTableKey, previousSeq, nextSeq })) {
    if (String(previousTableKey ?? '') !== String(nextTableKey ?? '')) {
      window.clearInterval(newestBlinkInterval);
      newestBlinkInterval = 0;
      newestBlinkHidden = false;
      blinkRoadIndexes = [-1, -1, -1, -1];
      nextTick(draw);
    }
    return;
  }
  blinkRoadIndexes = nextSources.map((value, index) => latestChangedVisibleRoadIndex(previousSources[index], value, { sourceColumns: 40, visibleColumns: 40, visibleRows: 6 }));
  if (blinkRoadIndexes.some((index) => index >= 1)) flashNewestRoadMarks();
});
watch(() => [props.analysis, props.mode, props.seed], () => nextTick(draw), { deep: true });
</script>

<template><canvas ref="canvas" class="hall-tile-road" :data-road-mode="mode" width="1082" height="328" /></template>
